import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@justgiving/loading/es/Loading';
import { useTracking } from '@justgiving/react-tracking';
import classNames from 'classnames';

import { PageStatus } from '../../../types/__generated__';
import { usePageMeta } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import isLightColor from '../../utils/colors/isLightColor';

import styles from './DonateButton.scss';
import { isCharity } from '../../utils/typeGuards';

type DonateButtonProps = {
  className?: string;
  inactivePageBehaviour?: 'disable' | 'hide';
  onClick?: () => void;
  trackingDataPageSection?: string;
  experiments?: string[];
  successType?: string;
  label?: string;
};

export const DonateButton: FunctionComponent<DonateButtonProps> = ({
  className,
  inactivePageBehaviour = 'disable',
  onClick,
  trackingDataPageSection,
  experiments,
  successType,
  label,
}) => {
  const basePageData = useBasePageQuery();
  const { currentUrl, pageType } = usePageMeta();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('common', { useSuspense: false });
  const track = useTracking();

  if (!basePageData.data?.page) {
    return null;
  }

  const { status, relationships, branding } = basePageData.data.page;
  const isDonateActive = status === PageStatus.Active;

  const isMultiBeneficiaryCampaign =
    relationships && relationships.beneficiaries.totalCount > 1;

  if (inactivePageBehaviour === 'hide' && !isDonateActive) {
    return null;
  }

  const beneficiaryNodes =
    basePageData.data?.page?.relationships?.beneficiaries.nodes;
  const firstBeneficiary =
    beneficiaryNodes && beneficiaryNodes.length > 0
      ? beneficiaryNodes[0]
      : null;
  const isChildrenInNeedPage = Boolean(
    ['fundraising', 'campaign'].includes(pageType) &&
      firstBeneficiary &&
      isCharity(firstBeneficiary) &&
      firstBeneficiary.legacyId === '181821',
  );

  const buttonType =
    branding?.primaryColor &&
    isLightColor(branding.primaryColor) &&
    !isChildrenInNeedPage
      ? 'jg-btn--ghost'
      : 'jg-btn--themed';

  function handleClick() {
    setLoading(true);

    track({
      page_section: `${pageType} page > ${trackingDataPageSection ||
        'unknown'}`.toLowerCase(),
      event: 'link click',
      action_name: 'link click',
      event_value: 'donate',
      subtype: 'donate',
    });

    if (onClick) {
      onClick();
    }
  }

  const buildQueryString = () => {
    const urlParams = new URLSearchParams();

    const trimmedSuccessType = successType?.trim();
    const trimmedExperiments = experiments
      ?.map(e => e.trim())
      .filter(e => e !== '');

    if (
      trimmedExperiments &&
      trimmedExperiments.length > 0 &&
      trimmedSuccessType
    ) {
      urlParams.append('experiments', trimmedExperiments.join(','));
      urlParams.append('successType', trimmedSuccessType);
    }

    return urlParams.toString() ? `?${urlParams.toString()}` : '';
  };

  const donateLink = isDonateActive ? (
    <a
      type="button"
      className={classNames(
        'jg-btn',
        buttonType,
        styles.button,
        className,
        {
          [styles.childrenInNeedButton]: isChildrenInNeedPage,
        },
        {
          [styles.childrenInNeedButtonNoBandage]:
            isChildrenInNeedPage && trackingDataPageSection === 'StickySummary',
        },
      )}
      onClick={handleClick}
      href={`${currentUrl}/donate${buildQueryString()}`}
      data-qa="donateButton"
    >
      {loading ? (
        <Loader size="small" colour="white" />
      ) : (
        label ?? t('common.donateButton')
      )}
    </a>
  ) : (
    <>
      <div
        className={classNames(
          'jg-btn',
          'jg-text--link',
          buttonType,
          styles.button,
          className,
          styles.disabled,
        )}
        data-qa="donateButtonDisabled"
      >
        <span aria-hidden>{label ?? t('common.donateButton')}</span>
        <span className="jg-sr-only">{t('common.donateButtonInactive')}</span>
      </div>
    </>
  );

  return !isMultiBeneficiaryCampaign ? donateLink : null;
};

export default DonateButton;
